import { isBrowser } from '@utils/isBrowser';
import Script from 'next/script';
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

interface ManifoldListingState {
  bids: { bidder: string; amount: string }[];
  offers: { offerer: string; rank: number; amount: string }[];
  countdown: { days: string; hours: string; minutes: string; seconds: string };
  marketplace: {
    listing?: {
      currentBidAmount?: { _hex: string };
      id: number;
      totalAvailable: number;
    };
  };
  handlingProviderEvent: {
    address: string;
  };
  transactionState: {
    error?: {
      reason?: string;
    };
  };
}

declare global {
  interface Window {
    mMarketplaceStore: {
      _state: { data: Record<string, ManifoldListingState> };
      subscribeAction: (
        callback: (
          action: { type: string; payload: string },
          state: Record<string, ManifoldListingState>,
        ) => void,
      ) => () => void;
    };
  }
}

interface ManifoldContextProps {
  listingStates?: Record<string, ManifoldListingState>;
  init: () => void;
  getListingState: (
    listingId: string,
  ) => ManifoldListingState | null | undefined;
}

export const ManifoldContext = createContext<ManifoldContextProps>({
  listingStates: undefined,
  init: () => {},
  getListingState: () => undefined,
});

export const Provider = ({ children }: { children?: React.ReactNode }) => {
  const [listingStates, setListingStates] =
    useState<Record<string, ManifoldListingState>>();
  const intervalId = useRef<NodeJS.Timer | null>(null);
  const [loaded, setLoaded] = useState(false);

  const subscribeToState = useCallback(() => {
    if (!intervalId.current) {
      intervalId.current = setInterval(() => {
        if (!isBrowser() || !window.mMarketplaceStore) return;
        const { mMarketplaceStore } = window;
        // eslint-disable-next-line no-underscore-dangle
        setListingStates({ ...mMarketplaceStore._state.data });
      }, 5000);
    }
  }, []);

  const getListingState = useCallback(
    (listingId: string) => {
      if (listingStates && listingId) {
        return Object.values(listingStates).find(
          (state) => state.marketplace?.listing?.id === Number(listingId),
        );
      }
      return null;
    },
    [listingStates],
  );

  const init = useCallback(() => {
    if (!loaded) setLoaded(true);
  }, [loaded]);

  const context = useMemo(
    () => ({ listingStates, init, getListingState }),
    [getListingState, init, listingStates],
  );

  useEffect(() => {
    subscribeToState();
    return () => {
      if (intervalId.current) clearInterval(intervalId.current);
    };
  }, [subscribeToState]);

  return (
    <>
      <ManifoldContext.Provider value={context}>
        {children}
      </ManifoldContext.Provider>
      {loaded ? (
        <>
          <Script src="https://connect.manifoldxyz.dev/3.2.2/connect.umd.min.js" />
          <Script src="https://marketplace.manifoldxyz.dev/3.5.19/marketplace.umd.min.js" />
        </>
      ) : null}
    </>
  );
};
